import { NativeStackHeaderProps, Stack } from 'expo-router';
import useChangeEmail from 'features/myVaro/hooks/useChangeEmail';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import Dialog from 'shared/components/vxl/Dialog/Dialog';
import {
  emailSchema,
  emailValidationMessages,
} from 'shared/components/vxl/input/schemas/email.schema';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import theme from 'shared/styles/theme';

const ChangeEmailScreen = () => {
  const [newEmail, setEmail] = useState<string>('');
  const insets = useSafeAreaInsets();
  const {
    isLoading,
    errorScreen,
    emailError,
    setEmailError,
    setErrorScreen,
    confirmNewEmailAddress,
  } = useChangeEmail();

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'changeEmail',
  });

  const emailValidationResult = emailSchema.safeParse(newEmail);
  const isInvalidEmail = !emailValidationResult.success;

  const handleSubmitEditing = () => {
    if (!emailValidationResult.success) {
      setEmailError(emailValidationMessages.invalid);
      return;
    }

    confirmNewEmailAddress(newEmail);
  };

  return (
    <ScrollView
      style={styles.body}
      contentContainerStyle={styles.arrangeVertical}
    >
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              options={{
                headerBackVisible: true,
                title: translatedText('title'),
              }}
            />
          ),
        }}
      />
      <View>
        <BodyBaseText numberOfLines={0} style={styles.newEmailLabel}>
          {translatedText('labels.editDescription')}
        </BodyBaseText>
        <VXLTextInput
          value={newEmail}
          onChangeText={(text) => {
            setEmail(text);
            setEmailError('');
          }}
          onSubmitEditing={handleSubmitEditing}
          errorText={emailError}
          labelText={translatedText('labels.inputLabel')}
          autoCapitalize="none"
          testID="text-input-newEmail"
        />
      </View>
      <View style={{ paddingBottom: insets.bottom + theme.spacing.l }}>
        <Button
          onPress={() => confirmNewEmailAddress(newEmail)}
          isDisabled={isInvalidEmail}
          label={translatedText('button.next')}
          isLoading={isLoading}
          analyticsId="changeEmailClicked"
        />
      </View>
      <Dialog
        isDialogVisible={!!errorScreen}
        titleText={errorScreen?.title}
        messageText={errorScreen?.message}
        dialogButtonProps={[
          {
            label: translatedText('button.ok'),
            onClick: () => setErrorScreen(null),
          },
        ]}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  arrangeVertical: {
    flex: 1,
    justifyContent: 'space-between',
  },
  body: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
  },
  newEmailLabel: {
    marginVertical: theme.spacing.m,
  },
});

export default ChangeEmailScreen;
